import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCopyBank } from "@/hooks/useCopyBank";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Lightbulb, ChevronRight, X, ArrowRight } from "lucide-react";

/**
 * TipOfTheDay — persistent education layer.
 *
 * Modes:
 *  - Single-page tip: one step, action on current page.
 *  - Multi-step tip: multiple steps with Next/Back within the banner.
 *  - Entry-point tip: Part 1 navigates to another page, Part 2 triggers there via sessionStorage.
 *
 * Props:
 *  - pageKey: string matching `page_key` in the tips collection.
 */
export default function TipOfTheDay({ pageKey }) {
  const { user } = useAuth();
  const { getText } = useCopyBank();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();

  const [tip, setTip] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Check for pending Part 2 entry-point tip
  useEffect(() => {
    if (!user || user.is_test_account) return;

    const pending = sessionStorage.getItem("pending_tip_part2");
    if (pending) {
      sessionStorage.removeItem("pending_tip_part2");
      // Fetch Part 2 tip
      axios
        .get(`${API}/tips/entry-point-part2`, { params: { part2_tip_id: pending } })
        .then((res) => {
          if (res.data) {
            setTip(res.data);
            setCurrentStep(0);
            setDismissed(false);
          }
        })
        .catch(() => {});
      return;
    }

    // Normal daily tip fetch
    axios
      .get(`${API}/tips/daily`, { params: { page_key: pageKey } })
      .then((res) => {
        if (res.data) {
          setTip(res.data);
          setCurrentStep(0);
          setDismissed(false);
        }
      })
      .catch(() => {});
  }, [user, pageKey]);

  const handleAction = useCallback(async () => {
    if (!tip) return;

    // Record action with variation_index for A/B tracking (silent gamification — no toasts)
    try {
      await axios.post(`${API}/tips/action`, { tip_id: tip.tip_id, variation_index: tip.variation_index || 0 });
    } catch { /* silent */ }

    // Entry-point tip: navigate to destination and stash Part 2 ID
    if (tip.is_entry_point && tip.entry_point_destination_page) {
      if (tip.entry_point_part2_tip_id) {
        sessionStorage.setItem("pending_tip_part2", tip.entry_point_part2_tip_id);
      }
      navigate(tip.entry_point_destination_page);
      return;
    }

    // Multi-step: advance to next step
    if (tip.steps && currentStep < tip.steps.length - 1) {
      setCurrentStep((s) => s + 1);
      return;
    }

    // Final step or single-page: highlight target if exists, then dismiss
    const step = tip.steps?.[currentStep];
    if (step?.highlight_target) {
      const el = document.querySelector(step.highlight_target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "box-shadow 0.3s";
        el.style.boxShadow = "0 0 20px rgba(245,158,11,0.5)";
        setTimeout(() => {
          el.style.boxShadow = "none";
        }, 2500);
      }
    }
    setDismissed(true);
  }, [tip, currentStep, navigate]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  if (!tip || dismissed) return null;
  if (!user || user.is_test_account) return null;

  const step = tip.steps?.[currentStep];
  const copyKey = step?.copy_bank_key;
  const tipText = copyKey ? getText(copyKey) : "Tip of the day";
  const isMultiStep = tip.steps && tip.steps.length > 1;
  const isLastStep = !isMultiStep || currentStep >= tip.steps.length - 1;
  const isEntryPoint = tip.is_entry_point;

  // Action button label
  let actionLabel = "Got it";
  if (isEntryPoint) actionLabel = "Show me";
  else if (isMultiStep && !isLastStep) actionLabel = "Next";

  return (
    <div
      className="relative bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-3"
      data-testid="tip-of-the-day"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2.5 right-2.5 p-1 text-slate-500 hover:text-slate-700 transition"
        data-testid="tip-dismiss-btn"
        aria-label="Dismiss tip"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-2.5 pr-6">
        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
          <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-0.5">
            Tip of the Day
            {isMultiStep && (
              <span className="ml-1.5 text-slate-500 normal-case tracking-normal">
                {currentStep + 1}/{tip.steps.length}
              </span>
            )}
          </p>
          <p className="text-xs text-slate-800 leading-relaxed" data-testid="tip-text">
            {tipText}
          </p>

          <div className="flex items-center gap-2 mt-2">
            {isMultiStep && currentStep > 0 && (
              <button
                onClick={() => setCurrentStep((s) => s - 1)}
                className="text-[10px] text-slate-500 hover:text-slate-700 transition font-medium"
                data-testid="tip-back-btn"
              >
                Back
              </button>
            )}
            <button
              onClick={handleAction}
              className="flex items-center gap-1 text-[11px] font-bold text-amber-700 hover:text-amber-900 transition"
              data-testid="tip-action-btn"
            >
              {actionLabel}
              {isEntryPoint ? (
                <ArrowRight className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
